import { ApolloClient, DefaultContext, useMutation } from '@apollo/client'
import { Alert } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import { format } from 'date-fns'
import React, { useContext } from 'react'

import {
  AccountDocumentType,
  DeleteAccountDocumentDocument,
} from '../../../../../../graphql/search'
import useLogAndCaptureError from '../../../../../../hooks/useLogAndCaptureError'
import { ApolloClientsContext } from '../../../../../../lib/graphql/apollo'
import { logAndCaptureException } from '../../../../../../utils/errorTools'
import { checkQueryResponse } from '../../../../../../utils/graphqlTools'
import * as Styled from './styled'

import fileIconBlack from '../../../../../../img/account_document_upload_icons/file-attach-icon-black.svg'

export interface FileSubmittedProps {
  className?: string
  fileName: string
  isDirty: boolean
  updatedAt: number | undefined
  documentType: AccountDocumentType
}

const formatTimestamp = (timestamp: number): string => format(new Date(timestamp), "PP 'at' HH:mm")

export const FileSubmitted: React.FC<FileSubmittedProps> = ({
  className,
  fileName,
  isDirty,
  updatedAt,
  documentType,
}) => {
  const { comma: commaClient } = useContext(ApolloClientsContext)

  const [
    deleteAccountDocument,
    { loading: deleteAccountDocumentLoading, error: deleteAccountDocumentError },
  ] = useMutation(DeleteAccountDocumentDocument, {
    client: commaClient as ApolloClient<object>,
    context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
  })
  useLogAndCaptureError(deleteAccountDocumentError)

  const handleRemove = async () => {
    await deleteAccountDocument({
      variables: { documentType: documentType },
    })
      .then(checkQueryResponse)
      .then(() => {
        commaClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customer' })
      })
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(`${err}`)
        logAndCaptureException(error)
      })
  }

  return (
    <>
      {deleteAccountDocumentError && (
        <Alert type="error">An error occurred removing your file, and we have been notified.</Alert>
      )}
      <Styled.OuterContainer className={className}>
        <Styled.InnerContainer>
          <Styled.Actions>
            <Styled.FileAttachIcon src={fileIconBlack} />
            {fileName} {!isDirty && 'on file'}
            <Styled.TextContainer>
              {!!updatedAt && (
                <Styled.SubmittedDate>Submitted {formatTimestamp(updatedAt)}</Styled.SubmittedDate>
              )}
            </Styled.TextContainer>
          </Styled.Actions>
          {!isDirty && (
            <Styled.RemoveButton onClick={handleRemove} disabled={deleteAccountDocumentLoading}>
              {deleteAccountDocumentLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Remove'
              )}
            </Styled.RemoveButton>
          )}
        </Styled.InnerContainer>
        <Styled.FileInformationText>
          Supported types .pdf, .docx, .jpeg, .png. Max. file size 10MB
        </Styled.FileInformationText>
      </Styled.OuterContainer>
    </>
  )
}
